/**
 * @file LinkPropertiesLink.js
 * @description Properties panel for link container link configuration.
 */

import { useEffect, useState } from 'react';
import './PropertiesMultimedia.css';
import styles from '../assets/AssetLibrary.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { queryPortalPages } from '../../services/portalPageService';
import { getClientOptions } from '../../services/clientService';
import { Dropdown } from '../helpers/Dropdown';
import SSODropdown from '../shared/asyncDropdowns/SSODropdown';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/Contexts';
import { ClientOptionEnum, TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const MENU_ROUTE_OPTIONS = [
    { label: getText('msg_927f40673764'), value: ROUTES.MY_BENEFITS },
    { label: getText('msg_f1702b468627'), value: ROUTES.MY_MESSAGES },
    { label: getText('msg_20e338624cee'), value: ROUTES.MY_ASSETS },
];

const MENU_ROUTE_VALUES = MENU_ROUTE_OPTIONS.map(option => option.value);

function getLabelForMenuRoute(routeValue) {
    const option = MENU_ROUTE_OPTIONS.find(opt => opt.value === routeValue);
    return option ? option.label : 'Select';
}

function LinkPropertiesLink({
    item,
    setItem,
    linkState,
    setLinkState,
    portalId,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [eligiblePortalsList, setEligiblePortalsList] = useState([]);
    const [menuRouteOptions, setMenuRouteOptions] =
        useState(MENU_ROUTE_OPTIONS);
    const { clientTierMeetsRequirement } = useAuthContext();
    const canUseSSO = clientTierMeetsRequirement(TenantTiers.Bronze);

    const {
        isWebsiteChecked,
        isPortalChecked,
        isSSOChecked,
        isMenuChecked,
        linkUrl,
        childPortalId,
        tenantSSOId,
        menuRoute,
        isInitialized,
    } = linkState;

    useEffect(() => {
        const fetchMenuRouteLabels = async () => {
            try {
                const options = await getClientOptions(0, [
                    ClientOptionEnum.RenameBenefitsMenu,
                    ClientOptionEnum.RenameMessagesMenu,
                    ClientOptionEnum.RenameAssetsMenu,
                ]);

                const benefitsOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameBenefitsMenu,
                );
                const messagesOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameMessagesMenu,
                );
                const assetsOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameAssetsMenu,
                );

                setMenuRouteOptions([
                    {
                        label:
                            benefitsOption && !benefitsOption.usingDefaultValue
                                ? benefitsOption.value || 'Benefits'
                                : 'Benefits',
                        value: ROUTES.MY_BENEFITS,
                    },
                    {
                        label:
                            messagesOption && !messagesOption.usingDefaultValue
                                ? messagesOption.value || 'Messages'
                                : 'Messages',
                        value: ROUTES.MY_MESSAGES,
                    },
                    {
                        label:
                            assetsOption && !assetsOption.usingDefaultValue
                                ? assetsOption.value || 'Assets'
                                : 'Assets',
                        value: ROUTES.MY_ASSETS,
                    },
                ]);
            } catch (error) {
                console.error('Failed to fetch menu route labels', error);
            }
        };

        fetchMenuRouteLabels();
    }, []);

    useEffect(() => {
        const portalsArray = [];
        queryPortalPages(true)
            .then(payload => {
                payload.viewModels.forEach(portalPage => {
                    if (portalPage.status === 1 && portalPage.id !== portalId) {
                        const label =
                            portalPage.isChild === false
                                ? `${portalPage.name} (M)`
                                : portalPage.name;
                        portalsArray.push({
                            value: portalPage.id,
                            label,
                        });
                    }
                });
                setEligiblePortalsList(portalsArray);
            })
            .catch(err => {
                console.error('Failed to query portal pages', err);
            });
    }, [portalId]);

    function updateLinkState(updates) {
        setLinkState(prev => ({ ...prev, ...updates }));
    }

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        setItem(prevState => {
            const { linkPart } = prevState;
            return {
                ...prevState,
                linkPart: {
                    ...linkPart,
                    url: isWebsiteChecked
                        ? linkUrl
                        : isMenuChecked
                          ? menuRoute
                          : '',
                    childPortalId: isPortalChecked ? childPortalId : null,
                    tenantSSOId: isSSOChecked ? tenantSSOId : null,
                },
            };
        });
    }, [
        isWebsiteChecked,
        isPortalChecked,
        isSSOChecked,
        isMenuChecked,
        linkUrl,
        childPortalId,
        tenantSSOId,
        menuRoute,
        isInitialized,
    ]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateLinkState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            const linkPart = item.linkPart || {};
            const hasUrl = linkPart.url && linkPart.url !== '';
            const isMenuRoute =
                hasUrl && MENU_ROUTE_VALUES.includes(linkPart.url);
            const hasPortal =
                linkPart.childPortalId !== null &&
                linkPart.childPortalId !== undefined;
            const hasSSO =
                linkPart.tenantSSOId !== null &&
                linkPart.tenantSSOId !== undefined;

            if (isMenuRoute) {
                updateLinkState({
                    isWebsiteChecked: false,
                    isPortalChecked: false,
                    isSSOChecked: false,
                    isMenuChecked: true,
                    linkUrl: '',
                    childPortalId: null,
                    tenantSSOId: null,
                    menuRoute: linkPart.url,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasUrl) {
                updateLinkState({
                    isWebsiteChecked: true,
                    isPortalChecked: false,
                    isSSOChecked: false,
                    isMenuChecked: false,
                    linkUrl: linkPart.url || '',
                    childPortalId: null,
                    tenantSSOId: null,
                    menuRoute: '',
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasPortal) {
                updateLinkState({
                    isWebsiteChecked: false,
                    isPortalChecked: true,
                    isSSOChecked: false,
                    isMenuChecked: false,
                    linkUrl: '',
                    childPortalId: linkPart.childPortalId,
                    tenantSSOId: null,
                    menuRoute: '',
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasSSO) {
                updateLinkState({
                    isWebsiteChecked: false,
                    isPortalChecked: false,
                    isSSOChecked: true,
                    isMenuChecked: false,
                    linkUrl: '',
                    childPortalId: null,
                    tenantSSOId: linkPart.tenantSSOId,
                    menuRoute: '',
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            updateLinkState({
                isWebsiteChecked: true,
                isPortalChecked: false,
                isSSOChecked: false,
                isMenuChecked: false,
                linkUrl: '',
                childPortalId: null,
                tenantSSOId: null,
                menuRoute: '',
                isInitialized: true,
            });
            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    function findPortalPageName(portalPageId) {
        const portalPage = eligiblePortalsList.find(
            page => page.value === portalPageId,
        );
        return portalPage ? portalPage.label : `Portal ID: ${portalPageId}`;
    }

    function handlePortalChange(value) {
        updateLinkState({ childPortalId: value });
    }

    function handleSSOChange(ssoId) {
        updateLinkState({ tenantSSOId: ssoId });
    }

    function handleURLChange(event) {
        updateLinkState({ linkUrl: event.target.value });
    }

    function handleMenuRouteChange(value) {
        updateLinkState({ menuRoute: value });
    }

    function getLabelForMenuRoute(routeValue) {
        const option = menuRouteOptions.find(opt => opt.value === routeValue);
        return option ? option.label : 'Select';
    }

    function handleWebsiteCheck() {
        updateLinkState({
            isWebsiteChecked: true,
            isPortalChecked: false,
            isSSOChecked: false,
            isMenuChecked: false,
            childPortalId: null,
            tenantSSOId: null,
            menuRoute: '',
        });
    }

    function handlePortalCheck() {
        updateLinkState({
            isWebsiteChecked: false,
            isPortalChecked: true,
            isSSOChecked: false,
            isMenuChecked: false,
            linkUrl: '',
            tenantSSOId: null,
            menuRoute: '',
        });
    }

    function handleSSOCheck() {
        updateLinkState({
            isWebsiteChecked: false,
            isPortalChecked: false,
            isSSOChecked: true,
            isMenuChecked: false,
            linkUrl: '',
            childPortalId: null,
            menuRoute: '',
        });
    }

    function handleMenuCheck() {
        updateLinkState({
            isWebsiteChecked: false,
            isPortalChecked: false,
            isSSOChecked: false,
            isMenuChecked: true,
            linkUrl: '',
            childPortalId: null,
            tenantSSOId: null,
        });
    }

    function getSelectedPortalLabel() {
        if (!childPortalId) {
            return 'Select Portal';
        }
        return findPortalPageName(childPortalId);
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_d453fce881fa')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_51967a6b1b8c')}
                </div>
            </div>

            <div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isWebsiteChecked}
                        onChange={handleWebsiteCheck}
                        disabled={areInputsDisabled}
                    />
                    <label className="form-check-label">{getText('msg_2e8a57cc5c47')}</label>
                </div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isPortalChecked}
                        onChange={handlePortalCheck}
                        disabled={areInputsDisabled}
                    />
                    <label className="form-check-label">{getText('msg_b55223c7bdc7')}</label>
                </div>

                    {canUseSSO && (
                        <div
                            className="form-check"
                            style={{ float: 'left', marginRight: '30px' }}
                        >
                            <input
                                className="form-check-input"
                                type="radio"
                                checked={isSSOChecked}
                                onChange={handleSSOCheck}
                                disabled={areInputsDisabled}
                            />
                            <label className="form-check-label">{getText('msg_5fa55a6001cc')}</label>
                        </div>
                    )}

                <div className="form-check" style={{ float: 'left' }}>
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isMenuChecked}
                        onChange={handleMenuCheck}
                        disabled={areInputsDisabled}
                    />
                    <label className="form-check-label">{getText('msg_57f5f5efbc59')}</label>
                </div>
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isWebsiteChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_08738f6eef34')}
                </div>
                <input
                    onChange={handleURLChange}
                    placeholder={getText('msg_324f0f9ff976')}
                    value={linkUrl}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                    className="form-control"
                    type="text"
                    aria-label={getText('msg_eca7a939ae6d')}
                    disabled={areInputsDisabled}
                />
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isPortalChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_b55223c7bdc7')}
                </div>
                <Dropdown
                    allowSearch
                    defaultValue={getSelectedPortalLabel()}
                    options={eligiblePortalsList}
                    onChange={handlePortalChange}
                />
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isSSOChecked ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_5fa55a6001cc')}
                </div>
                <SSODropdown
                    inlineLabel={false}
                    label=""
                    selectedSSO={tenantSSOId ?? 0}
                    onSSOChange={handleSSOChange}
                    client
                    displayRelative
                />
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isMenuChecked ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_57f5f5efbc59')}
                </div>
                <Dropdown
                    name="menuRouteDropdown"
                    defaultValue={getLabelForMenuRoute(menuRoute)}
                    options={menuRouteOptions
                        .slice()
                        .sort((a, b) => a.label.localeCompare(b.label))}
                    value={menuRoute}
                    onChange={handleMenuRouteChange}
                    disabled={areInputsDisabled}
                />
            </div>
        </>
    );
}

export default LinkPropertiesLink;
